import type { JSX } from 'react'

/**
 * Trigger types accepted by the @kc3/api automation-rules CreateBodySchema
 * (apps/api/src/routes/conversations/admin/automation-rules.ts:19).
 *
 * Engine-firing status (cross-referenced against AutomationEngine callers):
 *   - on_create                       → fired (conversation-engine.ts:146)
 *   - on_status_change                → fired (conversation-engine.ts:283,322)
 *   - on_message_received             → fired (ingestor.ts:133)
 *   - on_outbound_message_drafted     → fired (send-message.ts:120) [not in
 *                                       route enum yet, but engine handles it]
 *   - scheduled                       → fired via AutomationEngine.scheduleRule
 *                                       + worker cron (automation_scheduler)
 *   - on_update / on_message_sent / on_note_added / on_sla_breach /
 *     on_ola_breach / on_sla_threshold / on_assignment_change /
 *     on_approval_decision / on_transfer / on_escalation / on_merge
 *     → accepted by the route enum but no engine.evaluate() caller exists
 *       in this codebase yet (Phase-1 gap — rules can be authored but won't
 *       fire until the corresponding emitter is wired).
 */
interface TriggerMeta {
  label: string
  icon: string // PrimeIcons class
  /** Whether the engine actually fires this trigger today. */
  engineSupported: boolean
}

const TRIGGER_META: Record<string, TriggerMeta> = {
  on_create: {
    label: 'On create',
    icon: 'pi pi-plus',
    engineSupported: true,
  },
  on_status_change: {
    label: 'On status change',
    icon: 'pi pi-refresh',
    engineSupported: true,
  },
  on_message_received: {
    label: 'On inbound message',
    icon: 'pi pi-arrow-down',
    engineSupported: true,
  },
  on_outbound_message_drafted: {
    label: 'On outbound draft',
    icon: 'pi pi-arrow-up',
    engineSupported: true,
  },
  scheduled: {
    label: 'Scheduled',
    icon: 'pi pi-clock',
    engineSupported: true,
  },
  on_update: {
    label: 'On update',
    icon: 'pi pi-pencil',
    engineSupported: false,
  },
  on_message_sent: {
    label: 'On message sent',
    icon: 'pi pi-send',
    engineSupported: false,
  },
  on_note_added: {
    label: 'On note added',
    icon: 'pi pi-comment',
    engineSupported: false,
  },
  on_sla_breach: {
    label: 'On SLA breach',
    icon: 'pi pi-exclamation-triangle',
    engineSupported: false,
  },
  on_ola_breach: {
    label: 'On OLA breach',
    icon: 'pi pi-exclamation-triangle',
    engineSupported: false,
  },
  on_sla_threshold: {
    label: 'On SLA threshold',
    icon: 'pi pi-hourglass',
    engineSupported: false,
  },
  on_assignment_change: {
    label: 'On assignment change',
    icon: 'pi pi-users',
    engineSupported: false,
  },
  on_approval_decision: {
    label: 'On approval decision',
    icon: 'pi pi-check-circle',
    engineSupported: false,
  },
  on_transfer: {
    label: 'On transfer',
    icon: 'pi pi-arrow-right-arrow-left',
    engineSupported: false,
  },
  on_escalation: {
    label: 'On escalation',
    icon: 'pi pi-megaphone',
    engineSupported: false,
  },
  on_merge: {
    label: 'On merge',
    icon: 'pi pi-link',
    engineSupported: false,
  },
}

export interface TriggerTypeBadgeProps {
  triggerType: string
}

/**
 * Pure presentational pill mapping an automation rule's `triggerType` to a
 * human label + PrimeIcon. Unknown triggers fall back to the raw key with a
 * neutral icon — never crashes. Triggers the engine doesn't yet fire are
 * tagged with a "(not fired yet)" suffix so admins know their rule is a
 * Phase-1 stub.
 */
export default function TriggerTypeBadge({
  triggerType,
}: TriggerTypeBadgeProps): JSX.Element {
  const meta = TRIGGER_META[triggerType]

  if (!meta) {
    return (
      <span
        className="trigger-badge inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
        data-trigger-type={triggerType}
        data-known="false"
      >
        <i className="pi pi-question-circle" aria-hidden="true" />
        <span>{triggerType}</span>
      </span>
    )
  }

  const className = meta.engineSupported
    ? 'trigger-badge inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-800'
    : 'trigger-badge inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800'

  return (
    <span
      className={className}
      data-trigger-type={triggerType}
      data-known="true"
      data-engine-supported={meta.engineSupported ? 'true' : 'false'}
      title={
        meta.engineSupported
          ? undefined
          : 'Rule type accepted but not yet fired by the engine (Phase 1 gap).'
      }
    >
      <i className={meta.icon} aria-hidden="true" />
      <span>{meta.label}</span>
      {!meta.engineSupported && (
        <span className="text-[10px] opacity-70">(not fired yet)</span>
      )}
    </span>
  )
}
